import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { countMultipolygonVertices } from '~/_shared/types/polygon/polygon.utils';
import {
  isRadiusProximity, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { convertDistanceToUnitSystem } from '~/_shared/utils/distance/distance.helpers';
import { getProximityRadius } from '~/_shared/utils/proximity/getProximityRadius';
import {
  calculateAreaInSquaredMeters, getAreaInUnit,
} from '~/map/map/distanceCalculator/calculateDistance';

export const AGS_REPORT_MAX_RADIUS_IN_MILES = 75;

export const validateRadiusForAgsApi = (proximity: Proximity, spreadsheetRowId?: SpreadsheetRowId) => {
  if (isRadiusProximity(proximity)) {
    const radialDistance = getProximityRadius(proximity, spreadsheetRowId);

    if (!radialDistance) {
      return false;
    }

    const radius = convertDistanceToUnitSystem({
      distance: radialDistance.radius,
      currentUnitSystem: radialDistance.unit,
      targetUnitSystem: UnitSystem.imperial,
    });

    return radius <= AGS_REPORT_MAX_RADIUS_IN_MILES;
  }

  return true;
};

export const validatePolygonForAgsApi = (polygon: MultiPolygon) => {
  const polygonVertices = countMultipolygonVertices(polygon);

  if (polygonVertices > 8000) {
    return {
      isValid: false,
      reason: 'exportReport.polygonTooComplex',
    } as const;
  }

  const areaInSquareMeters = polygon.reduce((totalArea, polygon) => totalArea + calculateAreaInSquaredMeters(polygon.path), 0);
  const areaInSquareMiles = getAreaInUnit(areaInSquareMeters, UnitSystem.imperial);
  if (areaInSquareMiles > 100_000) {
    return {
      isValid: false,
      reason: 'exportReport.polygonTooLarge',
    } as const;
  }

  return { isValid: true } as const;
};
